import { escapeForRegex } from "client/Misc/utils";
import { isNotNilOrEmpty } from "./utils";
import { ReplacementPattern } from "../Validation/serverErrors";

export class DotPathWildcardPattern implements ReplacementPattern {
	private readonly _patternRegExp: RegExp;

	constructor(pattern: string) {
		if (this.doesContainIllegalWildcards(pattern)) {
			throw new Error("Wildcard can only be used instead of entire segment.");
		}

		this._patternRegExp = this.getPatternRegExp(pattern);
	}

	public tryReplace(path: string, replacement: string): TryResult<string> {
		const match: RegExpMatchArray | null = path.match(this._patternRegExp);

		if (!match) {
			return {
				wasSuccessful: false
			};
		}

		const replacementStack: (string | undefined)[] = match.slice(1);

		const result: string = replacement.split(".")
			.map((segment: string): string | undefined => {
				const match = segment.match(/^\{(\d+)\}$/);
				if (!match) {
					return segment;
				}

				const replacementIndex: number = Number(match[1]);

				if (replacementIndex >= replacementStack.length) {
					throw new Error(`Pattern does not have a replacement with index ${replacementIndex}.`);
				}

				return replacementStack[replacementIndex];
			})
			.filter(isNotNilOrEmpty)
			.join(".");

		return {
			wasSuccessful: true,
			result
		};
	}

	private doesContainIllegalWildcards(pattern: string): boolean {
		return pattern.split(".")
			.some((segment: string, index: number): boolean => {
				switch (segment) {
					case "*":
					case "**":
						return false;
					case "*?":
						return index === 0;
					default:
						return segment.indexOf("*") !== -1;
				}
			});
	}

	private getPatternRegExp(pattern: string): RegExp {
		// Preserve wildcards with placeholders
		pattern = pattern.replace(
			/(?:^|\.)\*[?*]?(?=\.|$)/g,
			(wildcard: string): string => {
				switch (wildcard) {
					case "*":
					case ".*":
					case ".*?":
					case "**":
					case ".**":
						return wildcardToPlaceholder[wildcard];
					case "*?":
						throw new Error("Pattern cannot start with an optional wildcard.");
					default:
						throw new Error(`Unknown wildcard ${wildcard}`);
				}
			}
		);

		// Escape all special characters for regex
		pattern = escapeForRegex(pattern);
		pattern = pattern.replace(
			matchPlaceholdersRegex,
			(match: string): string => {
				switch(match) {
					case leadingWildcardPlaceholder:
					case wildcardPlaceholder:
					case optionalWildcardPlaceholder:
					case leadingGreedyWildcardPlaceholder:
					case greedyWildcardPlaceholder:
						return placeholderToRegExp[match];
					default:
						throw new Error(`Unknown placeholder: ${match}`);
				}
			}
		);

		return new RegExp(`^${pattern}$`);
	}
}

type Wildcard = "*" | ".*" | ".*?" | "**" | ".**";

const leadingWildcardPlaceholder = "1e2046e9a67b4412821e0d3e215f674b" as const;
const wildcardPlaceholder = "ee4a7e92407442859dab7f47050f9ce0" as const;
const leadingGreedyWildcardPlaceholder = "4179af945a004dc7bd117d5c4f3a8746" as const;
const greedyWildcardPlaceholder = "da6d6a91333c457eb8565a839d3691cb" as const;
const optionalWildcardPlaceholder = "110bdfc3e4c94cdabc70bc308d022846" as const;

type WildcardPlaceholder =
	| typeof wildcardPlaceholder
	| typeof optionalWildcardPlaceholder
	| typeof leadingWildcardPlaceholder
	| typeof leadingGreedyWildcardPlaceholder
	| typeof greedyWildcardPlaceholder;

const matchPlaceholdersRegex = new RegExp(
	[
		leadingWildcardPlaceholder,
		wildcardPlaceholder,
		optionalWildcardPlaceholder,
		leadingGreedyWildcardPlaceholder,
		greedyWildcardPlaceholder
	]
		.join("|"),
	"g"
);

const wildcardToPlaceholder: {[key in Wildcard]: WildcardPlaceholder} = {
	"*": leadingWildcardPlaceholder,
	".*": wildcardPlaceholder,
	".*?": optionalWildcardPlaceholder,
	"**": leadingGreedyWildcardPlaceholder,
	".**": greedyWildcardPlaceholder
};

const placeholderToRegExp: {[key in WildcardPlaceholder]: string} = {
	[leadingWildcardPlaceholder]: "([^\\.]+)",
	[wildcardPlaceholder]: "\\.([^\\.]+)",
	[optionalWildcardPlaceholder]: "(?:\\.([^\\.]+))?",
	[leadingGreedyWildcardPlaceholder]: "(.+)",
	[greedyWildcardPlaceholder]: "\\.(.+)"
};